import { PropsWithChildren, useEffect, useRef } from 'react';
import { Collapse } from '@chakra-ui/react';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { onScrollToSmoothly } from '@/utils/global';
import * as Styles from './SectionBox.styles';

export type SectionBoxType = PropsWithChildren<{
  title: string;
  id?: string;
  leftElement?: JSX.Element;
  rightElement?: JSX.Element;
  isCollapsible?: boolean;
  isOpen?: boolean;
  onToggleOpen?: (value: boolean) => void;
  className?: string;
  autoScroll?: boolean;
  onAutoScroll?: () => void;
}>;

const SectionBox = ({
  children,
  title,
  id,
  leftElement,
  rightElement,
  isCollapsible = false,
  isOpen = false,
  onToggleOpen,
  className,
  autoScroll = false,
  onAutoScroll,
  ...props
}: SectionBoxType): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const renderBody = (): JSX.Element | null => {
    if (!children) return null;

    return <Styles.Body>{children}</Styles.Body>;
  };

  useEffect(() => {
    if (id && ref.current && autoScroll) {
      onScrollToSmoothly(id);
      onAutoScroll?.();
    }
  }, [id, ref, autoScroll, onAutoScroll]);

  return (
    <Styles.Box id={id} className={className} ref={ref}>
      <Styles.Header>
        <Styles.GapBox>
          <Styles.BoxTitle {...props}>{title}</Styles.BoxTitle>
          {!!leftElement && <div>{leftElement}</div>}
        </Styles.GapBox>
        <Styles.GapBox>
          {isCollapsible && (
            <Styles.ToggleShowButton onClick={() => onToggleOpen?.(!isOpen)}>
              {`${isOpen ? 'Hide' : 'Show'} details`}
              {isOpen ? <ExpandLess /> : <ExpandMore />}
            </Styles.ToggleShowButton>
          )}
          {rightElement && <div>{rightElement}</div>}
        </Styles.GapBox>
      </Styles.Header>
      {isCollapsible ? (
        <Collapse style={{ overflow: isOpen ? 'unset' : 'hidden' }} in={isOpen}>
          {renderBody()}
        </Collapse>
      ) : (
        renderBody()
      )}
    </Styles.Box>
  );
};

export default SectionBox;
